import { useEffect, useMemo, useState } from 'react';
import {
  X, Eye, Users, Clock, AlertTriangle, Check, Truck, Search,
  Radio, History, Activity, FileText, Heart, Flame, MapPin, Shield,
  Wrench, Stethoscope, Droplet, ListChecks, CheckCircle2, XCircle,
} from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { formatClock } from '@/lib/format';
import { VEHICLE_SPECS } from '@/data/vehicles';
import { estimateEtaSec, formatEtaSec } from '@/lib/routing';
import { computeRequirementCheck } from '@/data/dispositionCheck';
import { getMapHandle } from '@/hooks/mapHandleRegistry';
import { RECONNAISSANCE_DURATION_MS } from '@/state/gameStore';
import type {
  Measure,
  Mission,
  MissionPhase,
  MissionStatus,
  Vehicle,
} from '@/state/types';

interface MissionDetailProps {
  mission: Mission;
  onClose: () => void;
}

const STATUS_LABEL: Record<MissionStatus, string> = {
  open: 'Offen',
  dispatched: 'Maßnahmen eingeleitet',
  onscene: 'Vor Ort',
  closed: 'Abgeschlossen',
  failed: 'Fehlgeschlagen',
};

const STATUS_PILL: Record<MissionStatus, string> = {
  open: 'bg-white/15 text-white',
  dispatched: 'bg-white/15 text-white',
  onscene: 'bg-amber-300/30 text-amber-100',
  closed: 'bg-status-available/30 text-white',
  failed: 'bg-fg-muted/40 text-white',
};

const PHASE_LABEL: Record<MissionPhase, string> = {
  callout: 'Notruf',
  enroute: 'Anfahrt',
  reconnaissance: 'Erkundung',
  engagement: 'Einsatz',
  closing: 'Abschluss',
};

type Tab = 'details' | 'patients' | 'units' | 'radio';

export function MissionDetail({ mission, onClose }: MissionDetailProps) {
  const pushToast = useGameStore((s) => s.pushToast);
  const allVehicles = useGameStore((s) => s.vehicles);
  const stations = useGameStore((s) => s.stations);
  const dispatchVehicles = useGameStore((s) => s.dispatchVehiclesToMission);
  const gameTime = useGameStore((s) => s.gameTime);

  const stationById = useMemo(
    () => new Map(stations.map((s) => [s.id, s])),
    [stations],
  );

  // Available = currently free (status 1 or 2), no active alarm/assignment.
  const available = useMemo(
    () =>
      allVehicles.filter(
        (v) =>
          (v.status === 1 || v.status === 2) &&
          !v.assignedMissionId &&
          !v.alarmStartedAt &&
          v.repairStartedAt === undefined &&
          // Phase 12.3.2 — Vehicles im Modul-Tausch (Status 6 + workshopUntilAt) sind blockiert.
          v.workshopUntilAt === undefined &&
          v.condition > 0.25,
      ),
    [allVehicles],
  );

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<Tab>('details');

  const candidatesWithEta = useMemo(() => {
    const q = search.trim().toLowerCase();
    return available
      .map((v) => {
        const station = stationById.get(v.stationId);
        const from = station?.position ?? v.position;
        const eta = estimateEtaSec(from, mission.position, VEHICLE_SPECS[v.type].speedMultiplier);
        return { vehicle: v, etaSec: eta, station };
      })
      .filter(({ vehicle, station }) => {
        if (!q) return true;
        const spec = VEHICLE_SPECS[vehicle.type];
        return (
          spec.label.toLowerCase().includes(q) ||
          spec.fullName.toLowerCase().includes(q) ||
          (station?.name ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.etaSec - b.etaSec);
  }, [available, stationById, mission.position, search]);

  // Phase 7.1.6 — Disposition: NO auto-AAO pre-selection.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Drop selections that became unavailable.
  useEffect(() => {
    setSelected((cur) => {
      const stillAvailable = new Set(available.map((v) => v.id));
      const next = new Set<string>();
      for (const id of cur) if (stillAvailable.has(id)) next.add(id);
      return next.size === cur.size ? cur : next;
    });
  }, [available]);

  // Phase 7.1.6 — internal validation; result is NOT shown pre-dispatch.
  const isLocked = mission.status !== 'open';

  // After arrival: insufficient-measures warning if assigned vehicles don't meet
  // the internal requirement set.
  const insufficientWarning = useMemo(() => {
    if (mission.status !== 'onscene' && mission.status !== 'dispatched') return null;
    if (mission.status === 'dispatched') {
      const anyArrived = allVehicles.some(
        (v) => v.assignedMissionId === mission.id && v.status === 4,
      );
      if (!anyArrived) return null;
    }
    const result = computeRequirementCheck(mission, allVehicles);
    return result.met ? null : result;
  }, [mission, allVehicles]);

  const have = mission.assignedVehicleIds.length;
  const phase = mission.phase ?? inferPhase(mission, allVehicles);

  const handleAlarm = () => {
    // Phase 11.3.5 (Bug N10) — Auch in dispatched/onscene erlauben (Nachalarmieren).
    if (selected.size === 0) return;
    const ok = dispatchVehicles(mission.id, Array.from(selected));
    if (!ok) {
      pushToast({ kind: 'error', text: 'Alarmierung fehlgeschlagen.' });
      return;
    }
    pushToast({
      kind: 'success',
      text: `${selected.size} Fahrzeug${selected.size === 1 ? '' : 'e'} alarmiert.`,
    });
    setSelected(new Set());
    if (!isLocked) onClose();
  };

  const toggleVehicle = (id: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Live: scene effectiveness
  const sceneVehicles = allVehicles.filter(
    (v) => v.assignedMissionId === mission.id && v.status === 4,
  );
  const liveStatus = mission.status === 'onscene' ? buildLiveStatus(mission, sceneVehicles) : null;

  // Assigned vehicles (any status) — shown in "Einheiten" tab.
  const assignedVehicles = useMemo(
    () => allVehicles.filter((v) => v.assignedMissionId === mission.id),
    [allVehicles, mission.id],
  );

  // Reconnaissance countdown (in seconds, game-time)
  const reconRemainingSec =
    phase === 'reconnaissance' && mission.reconnaissanceStartedAt !== undefined
      ? Math.max(
          0,
          Math.ceil(
            (mission.reconnaissanceStartedAt + RECONNAISSANCE_DURATION_MS - gameTime) / 1000,
          ),
        )
      : null;

  return (
    <div className="flex h-full flex-col">
      {/* Phase 11.3.3 (N6) — Org-spezifisches Icon im Header statt generischem Sparkles. */}
      <header
        className="px-5 py-4 text-white"
        style={{
          background:
            mission.category === 'police'
              ? '#1d4ed8'
              : mission.category === 'rescue'
                ? '#f97316'
                : '#dc2626',
        }}
      >
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
            {mission.category === 'police' ? (
              <Shield className="h-5 w-5" aria-hidden />
            ) : mission.category === 'rescue' ? (
              <Heart className="h-5 w-5" aria-hidden />
            ) : (
              <Flame className="h-5 w-5" aria-hidden />
            )}
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="text-[10px] uppercase tracking-wider opacity-80">{mission.code}</p>
            <p className="font-display text-lg font-bold">{mission.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-white/15 transition hover:bg-white/25"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span
            className={[
              'rounded-full px-2.5 py-1 text-[10px] font-bold',
              STATUS_PILL[mission.status],
            ].join(' ')}
          >
            {STATUS_LABEL[mission.status]}
          </span>
          <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold">
            Phase: {PHASE_LABEL[phase]}
            {reconRemainingSec !== null && ` (${reconRemainingSec} s)`}
          </span>
          {mission.escalationCount > 0 && (
            <span className="rounded-full bg-amber-500/30 px-2.5 py-1 text-[10px] font-bold text-amber-100">
              ⚠ Eskaliert ×{mission.escalationCount}
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              const h = getMapHandle();
              if (h) h.flyTo(mission.position, 16);
            }}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold transition hover:bg-white/25"
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
            Karte zentrieren
          </button>
        </div>

        <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-white/20">
          <div
            className="h-full rounded-full bg-white transition-all"
            style={{ width: `${Math.round(mission.progress * 100)}%` }}
          />
        </div>
        <p className="mt-1 text-[10px] opacity-80 tabular-nums">
          {have} Fahrzeug{have === 1 ? '' : 'e'} disponiert · Fortschritt{' '}
          {Math.round(mission.progress * 100)} %
        </p>
      </header>

      {/* Tabs */}
      <nav className="border-b border-border-subtle bg-bg-secondary px-2 pt-1">
        <div className="flex gap-1 overflow-x-auto">
          <TabButton
            active={tab === 'details'}
            onClick={() => setTab('details')}
            icon={FileText}
            label="Details"
          />
          {mission.patientCount > 0 && (
            <TabButton
              active={tab === 'patients'}
              onClick={() => setTab('patients')}
              icon={Heart}
              label={`Patienten (${mission.patientsDelivered}/${mission.patientCount})`}
            />
          )}
          <TabButton
            active={tab === 'units'}
            onClick={() => setTab('units')}
            icon={Truck}
            label={`Einheiten (${have})`}
          />
          <TabButton
            active={tab === 'radio'}
            onClick={() => setTab('radio')}
            icon={Radio}
            label={`Funk (${mission.radioMessages?.length ?? 0})`}
          />
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {tab === 'details' && (
          <DetailsTab
            mission={mission}
            phase={phase}
            liveStatus={liveStatus}
            sceneVehicleCount={sceneVehicles.length}
            have={have}
            insufficientWarning={insufficientWarning}
            reconRemainingSec={reconRemainingSec}
          />
        )}

        {tab === 'patients' && (
          <PatientsTab mission={mission} sceneVehicleCount={sceneVehicles.length} />
        )}

        {tab === 'units' && (
          <UnitsTab
            phase={phase}
            assignedVehicles={assignedVehicles}
            stationById={stationById}
            isLocked={isLocked}
            available={available}
            candidatesWithEta={candidatesWithEta}
            selected={selected}
            search={search}
            setSearch={setSearch}
            toggleVehicle={toggleVehicle}
            handleAlarm={handleAlarm}
            gameTime={gameTime}
          />
        )}

        {tab === 'radio' && <RadioTab mission={mission} />}
      </div>
    </div>
  );
}

/* ──────────────── Tab content ──────────────── */

function DetailsTab({
  mission,
  phase,
  liveStatus,
  sceneVehicleCount,
  have,
  insufficientWarning,
  reconRemainingSec,
}: {
  mission: Mission;
  phase: MissionPhase;
  liveStatus: LiveStatus | null;
  sceneVehicleCount: number;
  have: number;
  insufficientWarning: { summary: string } | null;
  reconRemainingSec: number | null;
}) {
  return (
    <>
      {/* Phase 8.4.4 — Notruf-Wortlaut prominent oben */}
      {(phase === 'callout' || phase === 'enroute') && mission.callerStatement && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
          <p className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-300">
            <Activity className="h-3 w-3" aria-hidden /> Wortlaut Notruf
          </p>
          <p className="text-sm leading-relaxed text-amber-100">{mission.callerStatement}</p>
        </div>
      )}

      {/* Reconnaissance phase: explain what's happening */}
      {phase === 'reconnaissance' && (
        <div className="rounded-lg border border-blue-400/40 bg-blue-400/10 p-4">
          <p className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-blue-300">
            <Eye className="h-3 w-3" aria-hidden /> Erkundung läuft
          </p>
          <p className="text-sm leading-snug text-blue-100">
            Erstes Fahrzeug ist eingetroffen und erkundet die Lage.
            {reconRemainingSec !== null && ` Noch ca. ${reconRemainingSec} s bis zur Maßnahmenphase.`}
            {' '}Ein zusätzlich eintreffender ELW würde die Erkundung sofort beenden.
          </p>
        </div>
      )}

      {/* Closing screen — show reward */}
      {phase === 'closing' && (
        <div
          className={[
            'rounded-lg border p-4',
            mission.status === 'closed'
              ? 'border-status-available/40 bg-status-available/10 text-status-available'
              : 'border-fg-muted/40 bg-fg-muted/10 text-fg-secondary',
          ].join(' ')}
        >
          <p className="text-[10px] uppercase tracking-wider">
            {mission.status === 'closed' ? 'Einsatz abgeschlossen' : 'Einsatz beendet'}
          </p>
          <p className="mt-1 font-display text-xl font-bold">
            {mission.status === 'closed'
              ? `+${mission.payout.toLocaleString('de-DE')} €`
              : 'Keine volle Auszahlung'}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Stat icon={Users} label="Patienten" value={String(mission.patientCount)} />
        <Stat icon={Clock} label="Spawn" value={formatClock(mission.spawnedAt)} />
        <Stat icon={Clock} label="Frist" value={formatClock(mission.expiresAt)} />
        <Stat icon={MapPin} label="Code" value={mission.code} />
      </div>

      {/* Phase 8.4.6 — fireIntensity bar (dezent) */}
      {mission.category === 'fire' && mission.fireIntensity !== undefined && (
        <Card>
          <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
            <Flame className="h-3 w-3 text-accent-emergency" aria-hidden />
            Brandstärke
          </p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-primary">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.round(mission.fireIntensity * 100)}%`,
                background: `linear-gradient(90deg, #fbbf24, #ef4444 ${Math.min(100, mission.fireIntensity * 100)}%)`,
              }}
            />
          </div>
          <p className="mt-1 text-[10px] text-fg-muted">
            Sinkt mit Maßnahmenfortschritt.
          </p>
        </Card>
      )}

      {/* Phase 9.1.4 — Einsatzaktionen (Maßnahmen) — sichtbar erst nach Erkundung */}
      {(phase === 'engagement' || phase === 'closing') &&
        mission.measures &&
        mission.measures.length > 0 && (
          <MeasuresSection mission={mission} />
        )}

      {/* Live-Lagemeldung */}
      {liveStatus && (
        <Card>
          <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
            <Activity className="h-3 w-3" aria-hidden />
            Lage am Einsatzort
          </p>
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-fg-muted">Verbleibende Zeit</span>
              <span className="font-semibold tabular-nums">{liveStatus.remainingLabel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fg-muted">Effektivität</span>
              <span
                className={[
                  'font-semibold tabular-nums',
                  liveStatus.effectiveness >= 0.8
                    ? 'text-status-available'
                    : liveStatus.effectiveness >= 0.5
                      ? 'text-amber-300'
                      : 'text-accent-emergency',
                ].join(' ')}
              >
                {Math.round(liveStatus.effectiveness * 100)} %
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-fg-muted">Fahrzeuge vor Ort</span>
              <span className="font-semibold tabular-nums">
                {sceneVehicleCount} / {have}
              </span>
            </div>
            {liveStatus.note && <p className="mt-1 text-[10px] text-amber-300">{liveStatus.note}</p>}
          </div>
        </Card>
      )}

      {insufficientWarning && (
        <div className="flex items-start gap-2.5 rounded-md border border-amber-500/60 bg-amber-500/15 p-3 text-xs text-amber-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-400" aria-hidden />
          <div>
            <p className="font-semibold">Maßnahmen unzureichend</p>
            <p className="mt-1">{insufficientWarning.summary}</p>
            <p className="mt-1 opacity-80">
              Mission bleibt aktiv, aber Fortschritt stockt. Du kannst weitere Fahrzeuge nachalarmieren.
            </p>
          </div>
        </div>
      )}

      {(mission.escalationHistory?.length ?? 0) > 0 && (
        <Card>
          <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
            <History className="h-3 w-3" aria-hidden />
            Eskalations-Historie
          </p>
          <ul className="space-y-1">
            {(mission.escalationHistory ?? []).map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between text-xs"
              >
                <span className="font-semibold">
                  {e.fromCode} → {e.toCode}
                </span>
                <span className="text-[10px] text-fg-muted tabular-nums">
                  {formatClock(e.timestamp)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function PatientsTab({
  mission,
  sceneVehicleCount,
}: {
  mission: Mission;
  sceneVehicleCount: number;
}) {
  const total = mission.patientCount;
  const delivered = mission.patientsDelivered;
  const remaining = Math.max(0, total - delivered);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <SmallStat label="Gesamt" value={String(total)} />
        <SmallStat label="Versorgt" value={String(delivered)} tone="ok" />
        <SmallStat label="Offen" value={String(remaining)} tone="warn" />
      </div>

      {total === 0 ? (
        <p className="rounded-md border border-dashed border-border-subtle py-8 text-center text-xs text-fg-muted">
          Keine Patienten bei diesem Einsatz.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {Array.from({ length: total }).map((_, i) => {
            const isDelivered = i < delivered;
            return (
              <li
                key={i}
                className={[
                  'flex items-center gap-2.5 rounded-md border px-3 py-2',
                  isDelivered
                    ? 'border-status-available/40 bg-status-available/10'
                    : 'border-border-subtle bg-bg-elevated/40',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold',
                    isDelivered
                      ? 'bg-status-available text-bg-primary'
                      : 'bg-bg-elevated text-fg-secondary',
                  ].join(' ')}
                >
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold">Patient #{i + 1}</p>
                  <p className="text-[10px] text-fg-muted">
                    {isDelivered
                      ? 'In Klinik abgeliefert'
                      : sceneVehicleCount > 0
                        ? 'Vor Ort, wartet auf Transport'
                        : 'Vor Ort, kein Transportfahrzeug'}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function UnitsTab({
  phase,
  assignedVehicles,
  stationById,
  isLocked,
  available,
  candidatesWithEta,
  selected,
  search,
  setSearch,
  toggleVehicle,
  handleAlarm,
  gameTime,
}: {
  phase: MissionPhase;
  assignedVehicles: Vehicle[];
  stationById: Map<string, { id: string; name: string }>;
  isLocked: boolean;
  available: Vehicle[];
  candidatesWithEta: { vehicle: Vehicle; etaSec: number; station?: { name: string } }[];
  selected: Set<string>;
  search: string;
  setSearch: (v: string) => void;
  toggleVehicle: (id: string) => void;
  handleAlarm: () => void;
  gameTime: number;
}) {
  return (
    <div className="space-y-4">
      {assignedVehicles.length > 0 && (
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
            <Truck className="h-3 w-3" aria-hidden />
            Im Einsatz ({assignedVehicles.length})
          </p>
          <ul className="space-y-1.5">
            {assignedVehicles.map((v) => (
              <li
                key={v.id}
                className="flex items-center gap-2.5 rounded-md border border-border-subtle bg-bg-elevated/50 px-2.5 py-2 text-xs"
              >
                <span
                  className={[
                    'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md font-display text-base font-bold',
                    statusColorClass(v.status),
                  ].join(' ')}
                >
                  {v.status}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{VEHICLE_SPECS[v.type].label}</p>
                  <p className="truncate text-[10px] text-fg-muted">
                    {stationById.get(v.stationId)?.name ?? '—'}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Phase 11.3.5 (Bug N10) — Inline Disponieren / Nachalarmieren in
          allen aktiven Phasen (callout/enroute/reconnaissance/engagement),
          nicht nur bei `open`. Button-Label passt sich an. */}
      {(phase === 'callout' ||
        phase === 'enroute' ||
        phase === 'reconnaissance' ||
        phase === 'engagement') && (
        <>
          <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-elevated/40 px-3 py-2">
            <Search className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche Fahrzeug oder Wache…"
              className="w-full bg-transparent text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none"
            />
          </div>

          <div>
            <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
              <Truck className="h-3 w-3" aria-hidden />
              {isLocked ? 'Nachalarmieren' : 'Verfügbare Fahrzeuge'} (
              {candidatesWithEta.length})
            </p>
            {candidatesWithEta.length === 0 ? (
              <p className="rounded-md border border-dashed border-border-subtle py-8 text-center text-xs text-fg-muted">
                {available.length === 0
                  ? 'Keine freien Fahrzeuge.'
                  : 'Keine Treffer für diese Suche.'}
              </p>
            ) : (
              <ul className="space-y-1">
                {candidatesWithEta.map(({ vehicle, etaSec, station }) => {
                  const isSelected = selected.has(vehicle.id);
                  return (
                    <li key={vehicle.id}>
                      <CandidateRow
                        vehicle={vehicle}
                        stationName={station?.name ?? '—'}
                        etaSec={etaSec}
                        selected={isSelected}
                        onToggle={() => toggleVehicle(vehicle.id)}
                        gameTime={gameTime}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <button
            type="button"
            onClick={handleAlarm}
            disabled={selected.size === 0}
            className={[
              'w-full rounded-md px-4 py-3 text-sm font-bold transition',
              selected.size > 0
                ? 'bg-accent-emergency text-white shadow-lg hover:bg-accent-emergency-hover'
                : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
            ].join(' ')}
          >
            {selected.size === 0
              ? 'Keine Fahrzeuge ausgewählt'
              : isLocked
                ? `Nachalarmieren (${selected.size})`
                : `Alarmieren (${selected.size} Fahrzeug${selected.size === 1 ? '' : 'e'})`}
          </button>
        </>
      )}
    </div>
  );
}

function RadioTab({ mission }: { mission: Mission }) {
  const messages = mission.radioMessages ?? [];
  if (messages.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border-subtle py-8 text-center text-xs text-fg-muted">
        Noch keine Funkmeldungen.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {messages
        .slice()
        .reverse()
        .map((rm) => (
          <li
            key={rm.id}
            className="rounded-md border border-border-subtle bg-bg-elevated/40 px-2.5 py-2 text-xs"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">{rm.vehicleLabel}</span>
              <span className="text-[10px] text-fg-muted tabular-nums">
                {formatClock(rm.timestamp)}
              </span>
            </div>
            <p className="mt-0.5 text-fg-secondary">{rm.text}</p>
          </li>
        ))}
    </ul>
  );
}

/* ──────────────── helpers ──────────────── */

interface LiveStatus {
  remainingLabel: string;
  effectiveness: number;
  note?: string;
}

function buildLiveStatus(mission: Mission, sceneVehicles: Vehicle[]): LiveStatus {
  const remainingMs = mission.durationMs * (1 - mission.progress);
  const remainingLabel = formatRemaining(remainingMs);

  let effectiveness = 1;
  if (mission.stallToastShown) effectiveness = 0;
  else {
    const pumpers = sceneVehicles.filter(
      (v) => VEHICLE_SPECS[v.type].category === 'fire' && v.waterTankMax > 0,
    );
    if (pumpers.length > 0 && (mission.waterRequiredLiters ?? 0) > 0) {
      const avg =
        pumpers.reduce((acc, p) => {
          if (p.connectedHydrantId) return acc + 1.0;
          return acc + p.waterTankCurrent / p.waterTankMax;
        }, 0) / pumpers.length;
      effectiveness = avg;
    }
  }

  let note: string | undefined;
  if (mission.stallToastShown && effectiveness === 0) {
    note = 'Versorgung erschöpft — Mission stockt.';
  } else if (effectiveness < 0.3) {
    note = 'Wasservorrat kritisch.';
  }

  return { remainingLabel, effectiveness, note };
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'jetzt';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} s`;
  const min = Math.floor(sec / 60);
  const rest = sec % 60;
  if (min >= 10) return `${min} min`;
  return rest === 0 ? `${min} min` : `${min} min ${rest} s`;
}

/** Used as a fallback for legacy missions without a `phase` field. */
function inferPhase(mission: Mission, vehicles: Vehicle[]): MissionPhase {
  if (mission.status === 'closed' || mission.status === 'failed') return 'closing';
  if (mission.status === 'open') return 'callout';
  const anyOnScene = vehicles.some(
    (v) => v.assignedMissionId === mission.id && v.status === 4,
  );
  return anyOnScene ? 'engagement' : 'enroute';
}

function statusColorClass(s: number): string {
  switch (s) {
    case 1:
    case 2:
      return 'bg-status-available text-bg-primary';
    case 3:
      return 'bg-status-enroute text-white';
    case 4:
      return 'bg-status-onscene text-white';
    case 7:
    case 8:
      return 'bg-status-transport text-white';
    case 6:
      return 'bg-status-offduty text-white';
    default:
      return 'bg-bg-elevated text-fg-muted';
  }
}

interface CandidateRowProps {
  vehicle: Vehicle;
  stationName: string;
  etaSec: number;
  selected: boolean;
  onToggle: () => void;
  gameTime: number;
}

function CandidateRow({
  vehicle,
  stationName,
  etaSec,
  selected,
  onToggle,
}: CandidateRowProps) {
  const spec = VEHICLE_SPECS[vehicle.type];
  return (
    <button
      type="button"
      onClick={onToggle}
      className={[
        'flex w-full items-center gap-2.5 rounded-md border px-2.5 py-2 text-left transition',
        selected
          ? 'border-accent-emergency/60 bg-accent-emergency/10'
          : 'border-border-subtle bg-bg-elevated/50 hover:border-accent-emergency/40 hover:bg-bg-elevated',
      ].join(' ')}
    >
      <span
        className={[
          'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded',
          selected
            ? 'bg-accent-emergency text-white'
            : 'border border-border-strong bg-transparent',
        ].join(' ')}
        aria-hidden
      >
        {selected && <Check className="h-3 w-3" />}
      </span>
      <span
        className={[
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md font-display text-base font-bold',
          vehicle.status === 1 || vehicle.status === 2
            ? 'bg-status-available text-bg-primary'
            : 'bg-bg-elevated text-fg-muted',
        ].join(' ')}
      >
        {vehicle.status}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold">{spec.label}</p>
        <p className="truncate text-[10px] text-fg-muted">{stationName}</p>
      </div>
      <span className="flex-shrink-0 rounded-full bg-bg-elevated px-2 py-0.5 text-[11px] font-semibold tabular-nums">
        {formatEtaSec(etaSec)}
      </span>
    </button>
  );
}

interface StatProps {
  icon: typeof Users;
  label: string;
  value: string;
}

function Stat({ icon: Icon, label, value }: StatProps) {
  return (
    <div className="rounded-lg border border-border-subtle bg-bg-elevated/40 p-3">
      <div className="flex items-center gap-1.5 text-fg-muted">
        <Icon className="h-3 w-3" aria-hidden />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-0.5 font-display text-sm font-bold tabular-nums">{value}</p>
    </div>
  );
}

function SmallStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn';
}) {
  const toneClass =
    tone === 'ok'
      ? 'text-status-available'
      : tone === 'warn'
        ? 'text-amber-300'
        : 'text-fg-primary';
  return (
    <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3 text-center">
      <p className="text-[9px] uppercase tracking-wider text-fg-muted">{label}</p>
      <p className={['mt-0.5 font-display text-lg font-bold tabular-nums', toneClass].join(' ')}>
        {value}
      </p>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-border-subtle bg-bg-elevated/40 p-4">{children}</div>;
}

/* ──────────────── Phase 9.1 — Maßnahmen ──────────────── */

function measureIcon(category: Measure['category']) {
  if (category === 'fire') return Flame;
  if (category === 'medical') return Stethoscope;
  if (category === 'transport') return Truck;
  if (category === 'water-supply') return Droplet;
  if (category === 'rescue') return Wrench;
  return Wrench;
}

function MeasuresSection({ mission }: { mission: Mission }) {
  const measures = mission.measures ?? [];
  return (
    <Card>
      <p className="mb-3 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
        <ListChecks className="h-3 w-3" aria-hidden /> Einsatzaktionen ({measures.length})
      </p>
      <ul className="space-y-2.5">
        {measures.map((meas) => (
          <MeasureRow key={meas.id} measure={meas} />
        ))}
      </ul>
    </Card>
  );
}

function MeasureRow({ measure }: { measure: Measure }) {
  const Icon = measureIcon(measure.category);
  const allFulfilled = measure.requiredModules.every((r) => r.fulfilled);
  const tone =
    measure.status === 'completed'
      ? 'border-status-available/40 bg-status-available/10'
      : allFulfilled
        ? 'border-blue-400/40 bg-blue-400/10'
        : 'border-accent-emergency/30 bg-accent-emergency/5';
  const startedLabel = measure.startedAt
    ? new Date(measure.startedAt).toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  const missingFamily = measure.requiredModules.find((r) => !r.fulfilled);

  return (
    <li className={['rounded-md border p-3', tone].join(' ')}>
      <div className="flex items-start gap-3">
        <span
          className={[
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
            measure.status === 'completed'
              ? 'bg-status-available text-bg-primary'
              : allFulfilled
                ? 'bg-blue-500 text-white'
                : 'bg-bg-elevated text-fg-secondary',
          ].join(' ')}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-fg-primary">{measure.name}</p>
          <p className="text-[10px] text-fg-muted">
            {measure.status === 'completed'
              ? 'Abgeschlossen'
              : measure.status === 'active'
                ? `In Arbeit${startedLabel ? ` seit ${startedLabel}` : ''}`
                : measure.status === 'fulfilled'
                  ? 'Bereit, läuft gleich an'
                  : 'Wartet auf erforderliche Module'}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {measure.requiredModules.map((r, i) => (
              <span
                key={i}
                className={[
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold',
                  r.fulfilled
                    ? 'bg-status-available/20 text-status-available'
                    : 'bg-accent-emergency/20 text-accent-emergency',
                ].join(' ')}
              >
                {r.fulfilled ? (
                  <CheckCircle2 className="h-2.5 w-2.5" aria-hidden />
                ) : (
                  <XCircle className="h-2.5 w-2.5" aria-hidden />
                )}
                {r.family}
                {r.minSize ? ` ≥ ${r.minSize}` : ''}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-bg-primary">
        <div
          className={[
            'h-full transition-all',
            measure.status === 'completed' ? 'bg-status-available' : 'bg-blue-400',
          ].join(' ')}
          style={{ width: `${Math.round(measure.progress * 100)}%` }}
        />
      </div>
      {missingFamily && measure.status !== 'completed' && (
        <p className="mt-1.5 text-[10px] text-amber-300">
          ⚠️ Modul „{missingFamily.family}
          {missingFamily.minSize ? ` (≥ ${missingFamily.minSize})` : ''}" fehlt — passendes
          Fahrzeug nachalarmieren.
        </p>
      )}
    </li>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Users;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex items-center gap-1.5 whitespace-nowrap rounded-t-md px-3 py-2 text-[11px] font-semibold transition',
        active
          ? 'border-b-2 border-accent-emergency text-fg-primary'
          : 'border-b-2 border-transparent text-fg-muted hover:text-fg-secondary',
      ].join(' ')}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </button>
  );
}
